/**
 * 用户上下文管理
 *
 * 这个文件负责管理用户相关的状态和操作，包括：
 * - 用户信息管理（登录状态、用户数据）
 * - 用户权限管理（角色权限、功能权限）
 * - 组件管理（保存、删除、重命名组件）
 * - 用户登录流程处理
 */

import { ReactNode, createContext, useState } from 'react'
import { delComponentApi, overridComponent, saveComponent } from '../controllers/API'
import { captureAndAlertRequestErrorHoc } from '../controllers/request'
import { User } from '../types/api/user'

import { getPermissionsApi } from '@/controllers/API/common'
// import { backendPermissionList } from '@/permissions/constants'
import { showMessage } from '@/components/antd-comp/message'
import { handlePermissionToFrontend } from '@/permissions/utils'
import { useFuncsStore } from '@/store/funcStore'
import { triggerLenovoBack } from '../utils/lenovoBack'

/**
 * 用户上下文类型定义
 */
type userContextType = {
  /** 当前用户信息，可能的状态：{}（空对象）、loading（加载中）、null（未登录）、User（已登录用户） */
  user: User
  /** 设置用户信息 */
  setUser: (newState: User) => void
  /** 已保存的组件列表 */
  savedComponents: any
  /** 添加保存的组件 */
  addSavedComponent: (newCom: any, overrid: boolean, rename?: boolean) => Promise<any>
  /** 检查组件名称是否已存在 */
  checkComponentsName: (name: string) => boolean
  /** 删除组件 */
  delComponent: (name: string) => void
  /** 设置用户信息（包含权限处理） */
  setUserInfo: (userInfo: any) => Promise<any>
}

// 用户上下文的初始值
const initialValue = {
  user: {}, // 初始用户状态为空对象
  setUser: () => {},
  savedComponents: [],
  addSavedComponent: () => null,
  checkComponentsName: () => false,
  delComponent: () => {},
  setUserInfo: () => null
}

// 创建用户上下文
export const userContext = createContext<userContextType>(initialValue)

/**
 * 用户提供者组件
 *
 * @param children - 子组件
 * @returns 提供用户相关状态和方法的上下文提供者
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(initialValue.user)
  const [savedComponents, setSavedComponents] = useState([])

  /**
   * 权限管理相关状态和方法
   */
  const { setNeedFuncAuth, setFuncCodes } = useFuncsStore()

  /**
   * 加载组件列表
   * 注意：目前该接口未使用，因为性能问题（返回数据量过大）
   */
  const loadComponents = async () => {
    // const res = await getComponents()
    // setSavedComponents(res)
    // todo 数据未使用，后期存在使用时打开上面的接口数据加载
    console.log(
      '>>>>>>>> 目前页面未使用到/api/v1/component结果，启用该接口时需先讨论优化方案，2024830测试该接口dev环境耗时1分钟，返回数据量119M'
    )
    setSavedComponents([])
  }

  /**
   * 检查组件名称是否已存在
   * @param name - 要检查的组件名称
   * @returns 如果名称已存在返回 true，否则返回 false
   */
  const checkComponentsName = (name: string) => {
    return savedComponents.some(item => item.name === name)
  }

  /**
   * 添加保存的组件
   * @param component - 要保存的组件数据
   * @param overrid - 是否覆盖已存在的同名组件
   * @param rename - 是否自动重命名（默认为 true）
   * @returns Promise，保存操作的结果
   */
  const addSavedComponent = (component: any, overrid: boolean, rename: boolean = true) => {
    const nodeName = component.node.display_name
    const newNode = {
      ...component,
      node: { ...component.node, official: false }
    }

    const data = {
      name: nodeName,
      data: newNode,
      description: ''
    }

    // 覆盖已存在的组件
    if (overrid) {
      return captureAndAlertRequestErrorHoc(overridComponent(data)).then(res => {
        setSavedComponents(comps => comps.map(comp => (comp.name === data.name ? data : comp)))
      })
    } else {
      // 自动重命名以避免名称冲突
      if (rename) {
        const regex = /\((\d+)\)$/
        do {
          const match = data.name.match(regex)
          if (match) {
            // 如果找到匹配项，将数字提取出来，转换成数字类型，并加1
            const num = parseInt(match[1], 10) + 1
            data.name = data.name.replace(regex, `(${num})`)
          } else {
            data.name += '(1)'
          }
        } while (savedComponents.some(item => item.name === data.name))
      }
      return captureAndAlertRequestErrorHoc(saveComponent(data)).then(sucess => {
        sucess && setSavedComponents([...savedComponents, data])
      })
    }
  }

  /**
   * 删除组件
   * @param name - 要删除的组件名称
   */
  const delComponent = name => {
    delComponentApi(name).then(res => {
      setSavedComponents(comps => comps.filter(item => item.name !== name))
    })
  }

  /**
   * 设置用户信息（包含权限处理）
   * @param userInfo - 用户信息对象
   * @returns Promise，设置用户信息的操作结果
   */
  const setUserInfo = (userInfo: any) => {
    // 保存用户信息到本地存储
    localStorage.setItem('userInfo', JSON.stringify(userInfo))

    // 设置用户状态
    setUser({
      user_id: userInfo.user_id,
      user_name: userInfo.user_name,
      role: userInfo.roles || [],
      create_time: userInfo.create_time,
      access_token: userInfo.access_token,
      update_time: userInfo.update_time,
      enterprise_id: userInfo.enterprise_id,
      enterprise_name: userInfo.enterprise_name,
      remark: userInfo.remark,
      name: userInfo.name || userInfo.user_name,
      avatar: userInfo.avatar,
      lenovo_user_id: userInfo?.lenovo_user_id,
      roleName: userInfo?.roleName
    })

    // 判断是否需要权限验证（非管理员用户需要）
    const needAuth = !userInfo?.roles?.includes('admin')

    return new Promise<void>((resolve, reject) => {
      if (needAuth) {
        // 非管理员用户需要设置功能权限
        setNeedFuncAuth(true)
        setUserFuncList()
          .then(() => {
            completeLoginSetup(userInfo)
            resolve()
          })
          .catch(error => {
            console.error('设置角色和权限失败', error)
            reject(error)
          })
      } else {
        // 管理员用户直接完成登录设置
        completeLoginSetup(userInfo)
        resolve()
      }
    })
  }

  /**
   * 获取用户角色权限
   * @returns Promise，获取权限列表的结果
   */
  const setUserFuncList = () => {
    console.log('>>>>>>非root用户限制功能权限')
    return getPermissionsApi()
      .then(res => {
        console.log('getPermissionsApi', res)
        const backendPermissionList = Array.isArray(res) ? res : []
        // if (backendPermissionList.length === 0) {
        //   throw new Error('permission/allow_codes 返回空列表')
        // }

        const permissionList = handlePermissionToFrontend(backendPermissionList)
        // if (permissionList.length === 0) {
        //   throw new Error('permission/allow_codes 未返回有效前端权限')
        // }

        setFuncCodes(permissionList)
        // setFuncCodes(handlePermissionToFrontend(backendPermissionList))
      })
      .catch(error => {
        console.error('error', error)
        if (error?.__handled) {
          throw error
        }
        showMessage({
          content: error.message || '权限验证失败',
          type: 'error'
        })
        // 等 2s 让用户看到提示后再跳转，不立即 throw 避免上层 reject 提前导航
        return new Promise((_, reject) => {
          setTimeout(() => {
            triggerLenovoBack()
            reject(error)
          }, 2000)
        })
      })
  }

  /**
   * 完成登录状态设置
   * @param userInfo - 用户信息对象
   */
  const completeLoginSetup = (userInfo: any) => {
    // 设置登录状态
    localStorage.setItem('isLogin', '1')
    // UUR_INFO存储用户信息，暂时先存储id，后期扩展时需考虑缓存用户关键信息
    localStorage.setItem('UUR_INFO', String(userInfo.user_id))

    // 如果用户ID存在，加载组件列表
    if (userInfo.user_id) loadComponents()
  }

  return (
    <userContext.Provider
      value={{
        user,
        setUser,
        savedComponents,
        checkComponentsName,
        delComponent,
        addSavedComponent,
        setUserInfo
      }}
    >
      {children}
    </userContext.Provider>
  )
}
